import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface ShareLink {
  id: number;
  token: string;
  resource_type: string;
  resource_id: string | null;
  permission: string;
  expires_at: string | null;
  is_active: boolean;
  access_count: number;
  created_at: string;
}

export default function ShareDashboard() {
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resourceType, setResourceType] = useState('dashboard');
  const [permission, setPermission] = useState('view');
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const res = await api.get('/api/share');
      setLinks(res.data.links);
    } catch {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const createLink = async () => {
    try {
      const res = await api.post('/api/share', { resource_type: resourceType, permission });
      setCreatedLink(res.data.url);
      setShowCreate(false);
      fetchLinks();
    } catch {
      // silent
    }
  };

  const revokeLink = async (token: string) => {
    try {
      await api.delete(`/api/share/${token}`);
      setConfirmRevoke(null);
      fetchLinks();
    } catch {
      // silent
    }
  };

  const copyLink = () => {
    if (createdLink) {
      navigator.clipboard.writeText(window.location.origin + createdLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getPermissionColor = (perm: string) => {
    switch (perm) {
      case 'admin': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'edit': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Share Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create and manage sharing links</p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setCreatedLink(null); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          + New Link
        </button>
      </div>

      {createdLink && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Share Link Created</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-sm break-all text-gray-800 dark:text-gray-200">{window.location.origin}{createdLink}</code>
            <button onClick={copyLink} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <p className="font-medium text-gray-800 dark:text-gray-100 mb-3">Create Share Link</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Resource Type</label>
              <select value={resourceType} onChange={(e) => setResourceType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                <option value="dashboard">Dashboard</option>
                <option value="report">Report</option>
                <option value="project">Project</option>
                <option value="knowledge">Knowledge Base</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Permission Level</label>
              <select value={permission} onChange={(e) => setPermission(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                <option value="view">View Only</option>
                <option value="edit">Edit</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={createLink} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
                Create Link
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="text-gray-500 dark:text-gray-400">Loading...</div></div>
      ) : links.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <p className="text-4xl mb-2">🔗</p>
          <p>No sharing links yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <div key={link.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100">{link.resource_type}</h3>
                  <p className="text-sm font-mono text-gray-500 dark:text-gray-400 mt-1">{link.token}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${getPermissionColor(link.permission)}`}>
                    {link.permission}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${link.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}`}>
                    {link.is_active ? 'Active' : 'Revoked'}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                <span>{link.access_count} views</span>
                <span>Created {new Date(link.created_at).toLocaleDateString()}</span>
              </div>
              {link.is_active && (
                <div className="mt-3">
                  {confirmRevoke === link.token ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-600 dark:text-red-400">Revoke this link?</span>
                      <button onClick={() => revokeLink(link.token)} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                        Yes, Revoke
                      </button>
                      <button onClick={() => setConfirmRevoke(null)} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRevoke(link.token)} className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                      Revoke
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
